import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '../lib/supabase';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ShoppingBag, Heart, Truck, Shield, Gift, Clock, User, ChevronRight } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';

interface AuthModalProps {
  onClose: () => void;
}

const benefits = [
  { icon: ShoppingBag, title: 'История заказов', desc: 'Отслеживайте все покупки' },
  { icon: Heart, title: 'Избранное', desc: 'Сохраняйте понравившиеся товары' },
  { icon: Truck, title: 'Быстрое оформление', desc: 'Сохранённые адреса доставки' },
  { icon: Gift, title: 'Персональные скидки', desc: 'Эксклюзивные предложения' },
];

export function AuthModal({ onClose }: AuthModalProps) {
  const { settings } = useSiteSettings();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Анимация появления
    setTimeout(() => setIsVisible(true), 10);
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      console.log('Auth event:', event);
      if (event === 'SIGNED_IN') {
        onClose();
      }
    });

    // Блокируем скролл body
    document.body.style.overflow = 'hidden';

    return () => {
      subscription.unsubscribe();
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row transition-all duration-300 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
      >
        {/* Левая панель — Преимущества (скрыта на мобильных) */}
        <div className="hidden md:flex md:w-2/5 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 flex-col justify-between relative overflow-hidden">
          {/* Декоративные элементы */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFCC00]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FFCC00]/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            {/* Логотип */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-[#FFCC00] rounded-xl flex items-center justify-center shadow-lg shadow-[#FFCC00]/20 p-2">
                <img src="/logo.svg" alt="Big PineApple" className="w-full h-full" />
              </div>
              <div>
                <h2 className="text-white font-bold text-xl">Big PineApple</h2>
                <p className="text-gray-400 text-sm">Магазин ноутбуков</p>
              </div>
            </div>

            {/* Заголовок */}
            <div className="mb-8">
              <h3 className="text-white text-2xl font-bold mb-2">
                Добро пожаловать!
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Создайте аккаунт или войдите, чтобы получить доступ к персональным предложениям и удобному оформлению заказов.
              </p>
            </div>

            {/* Преимущества */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <div className="w-10 h-10 bg-[#FFCC00]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-[#FFCC00]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium text-sm">{benefit.title}</h4>
                    <p className="text-gray-400 text-xs">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Нижняя часть */}
          <div className="relative z-10 mt-8 pt-6 border-t border-white/10">
            <div className="flex items-center gap-3 text-gray-400 text-sm">
              <Shield className="w-5 h-5 text-green-400" />
              <span>Ваши данные надёжно защищены</span>
            </div>
          </div>
        </div>

        {/* Правая панель — Форма */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto">
          {/* Мобильный заголовок */}
          <div className="md:hidden flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FFCC00] rounded-xl flex items-center justify-center p-1.5">
                <img src="/logo.svg" alt="Big PineApple" className="w-full h-full" />
              </div>
              <span className="font-bold text-lg">Big PineApple</span>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Desktop кнопка закрытия */}
          <button
            onClick={handleClose}
            className="hidden md:flex absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-xl transition-colors z-20"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Заголовок формы */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FFCC00] to-[#FFB800] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#FFCC00]/20">
              <User className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Вход в аккаунт</h2>
            <p className="text-gray-500">Введите данные для входа или зарегистрируйтесь</p>
          </div>

          {/* Мобильные преимущества (горизонтальный скролл) */}
          <div className="md:hidden mb-6 -mx-6 px-6">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full whitespace-nowrap flex-shrink-0"
                >
                  <benefit.icon className="w-4 h-4 text-[#FFCC00]" />
                  <span className="text-sm text-gray-700">{benefit.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Форма авторизации */}
          <div className="auth-form-container">
            <Auth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                variables: {
                  default: {
                    colors: {
                      brand: '#FFCC00',
                      brandAccent: '#E6B800',
                      inputBackground: '#F9FAFB',
                      inputBorder: '#E5E7EB',
                      inputBorderFocus: '#FFCC00',
                      inputBorderHover: '#D1D5DB',
                    },
                    borderWidths: {
                      buttonBorderWidth: '0px',
                      inputBorderWidth: '1px',
                    },
                    radii: {
                      borderRadiusButton: '12px',
                      buttonBorderRadius: '12px',
                      inputBorderRadius: '12px',
                    },
                    fontSizes: {
                      baseButtonSize: '16px',
                      baseInputSize: '16px',
                    },
                    space: {
                      inputPadding: '14px 16px',
                      buttonPadding: '14px 20px',
                    },
                  }
                },
                className: {
                  container: 'auth-container',
                  button: 'auth-button font-semibold shadow-lg shadow-[#FFCC00]/20 hover:shadow-xl hover:shadow-[#FFCC00]/30 transition-all duration-200',
                  input: 'auth-input focus:ring-2 focus:ring-[#FFCC00]/20',
                  label: 'auth-label font-medium text-gray-700 mb-1.5',
                  anchor: 'auth-anchor text-[#FFCC00] hover:text-[#E6B800] font-medium',
                  message: 'auth-message text-sm',
                  divider: 'auth-divider',
                }
              }}
              localization={{
                variables: {
                  sign_up: {
                    email_label: 'Email адрес',
                    password_label: 'Придумайте пароль',
                    button_label: 'Создать аккаунт',
                    loading_button_label: 'Создание аккаунта...',
                    social_provider_text: 'Продолжить через {{provider}}',
                    link_text: 'Ещё нет аккаунта? Зарегистрируйтесь',
                    confirmation_text: 'Проверьте почту для подтверждения регистрации',
                  },
                  sign_in: {
                    email_label: 'Email адрес',
                    password_label: 'Пароль',
                    button_label: 'Войти в аккаунт',
                    loading_button_label: 'Входим...',
                    social_provider_text: 'Продолжить через {{provider}}',
                    link_text: 'Уже есть аккаунт? Войдите',
                  },
                  forgotten_password: {
                    email_label: 'Email адрес',
                    password_label: 'Пароль',
                    button_label: 'Отправить ссылку для сброса',
                    loading_button_label: 'Отправляем...',
                    link_text: 'Забыли пароль?',
                    confirmation_text: 'Ссылка для сброса пароля отправлена на вашу почту',
                  },
                  update_password: {
                    password_label: 'Новый пароль',
                    button_label: 'Сохранить новый пароль',
                    loading_button_label: 'Сохраняем...',
                    confirmation_text: 'Пароль успешно обновлён!',
                  },
                },
              }}
              theme="light"
              providers={[]}
              view="sign_in"
              redirectTo={window.location.origin + '/auth/callback'}
            />
          </div>

          {/* Разделитель */}
          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase tracking-wider">или</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Гостевой заказ */}
          <button 
            onClick={handleClose}
            className="w-full flex items-center justify-center gap-3 py-3.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-colors group"
          >
            <Clock className="w-5 h-5 text-gray-500" />
            <span>Продолжить без регистрации</span>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Правовая информация */}
          <p className="text-xs text-gray-400 text-center mt-6 leading-relaxed">
            Регистрируясь или входя на сайт, вы соглашаетесь с{' '}
            <Link to="/privacy" className="text-[#FFCC00] hover:underline font-medium" onClick={handleClose}>
              Политикой конфиденциальности
            </Link>{' '}
            и{' '}
            <Link to="/terms" className="text-[#FFCC00] hover:underline font-medium" onClick={handleClose}>
              Пользовательским соглашением
            </Link>
          </p>

          {/* Контакты поддержки */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Возникли проблемы со входом?{' '}
              <a href={`tel:${settings.phone_number.replace(/[^\d+]/g, '')}`} className="text-gray-600 hover:text-[#FFCC00] font-medium">
                {settings.phone_number}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}